/* Rama prawna (artboardy 03a–03i): nagłówek publiczny, tytuł dokumentu z wersją,
 * pasek czterech zakładek, siatka „spis 240 px · treść do prawej krawędzi", stopka.
 *
 * Formularz zgłoszenia treści NIE jest dokumentem — ma przycisk wysyłki, błędy pól
 * i stan po wysłaniu, więc nie przechodzi przez renderer bloków `LegalPage`. Ale MUSI
 * stać w tej samej ramie: strona zgłoszenia, która wygląda jak inna witryna, to strona,
 * której nikt nie ufa na tyle, żeby jej użyć, a art. 16 wymaga mechanizmu łatwego
 * w dostępie i przyjaznego.
 *
 * Tytuł wrócił do ramy (paczka 37): artboard stawia go NAD zakładkami, razem z linią
 * „wersja … · Obowiązuje od …”. `data-version` na korzeniu zostaje dla audytu.
 */
import type { ReactNode } from 'react'
import { useRef } from 'react'
import { Link } from 'react-router'
import { useT } from '../lib/i18n'
import { usePublicPath } from '../lib/publicPaths'
import { useEdgeFade } from '../lib/useEdgeFade'
import Seo from './Seo'
import PublicHeader from './public/PublicHeader'
import PublicFooterBar from './public/PublicFooterBar'
import '../styles/minimal-public.css'

export type LegalTab = 'regulamin' | 'polityka' | 'zwroty' | 'zgloszenie'

// Jedna lista, żeby nowa strona prawna pojawiła się w pasku przez dopisanie tutaj,
// a nie przez pamiętanie o czterech blokach JSX.
const TABS: { id: LegalTab; path: string; label: string }[] = [
  { id: 'regulamin', path: '/regulamin', label: 'legal.hero.tabRegulamin' },
  { id: 'polityka', path: '/polityka', label: 'legal.hero.tabPolityka' },
  { id: 'zwroty', path: '/zwroty', label: 'legal.hero.tabZwroty' },
  { id: 'zgloszenie', path: '/zgloszenie', label: 'legal.hero.tabZgloszenie' },
]

interface Props {
  tab: LegalTab
  /** Tytuł dokumentu (H1 strony). */
  title: string
  /** Identyfikator wersji (`legal.*.sub`) — w nagłówku i w `data-version`. */
  version: string
  /** Data obowiązywania słowami (`legal.*.date`). */
  date: string
  /** Na ekranie stoi wypełniona akcja strony (wysłanie zgłoszenia): przycisk nagłówka
   *  ma wtedy obrys — jedna wypełniona akcja naraz (PROD2 F2-15). */
  quietCta?: boolean
  children: ReactNode
}

export default function LegalShell({ tab, title, version, date, quietCta = false, children }: Props) {
  // Na 390 pasek zakładek się przewija i zakładka, NA KTÓREJ jesteś, bywa tą poza
  // ekranem. Reguła 60 (PL1): pasek zanika po stronie, po której coś zostało, a aktywna
  // zakładka staje w widoku od pierwszej klatki (`nearest`, bez animacji) — do PL1
  // wyśrodkowanie aktywnej ucinało sąsiadów w pół słowa („a prywatności”, „Zgłoś tre”).
  const tabsRef = useRef<HTMLElement>(null)
  useEdgeFade(tabsRef, tab)

  const t = useT()
  const pub = usePublicPath()

  return (
    <div className="mp" data-version={version}>
      <Seo page={tab} />
      <PublicHeader quietCta={quietCta} />

      <main className="mp-doc">
        <div className="mp-doc-head">
          <div>
            <h1>{title}</h1>
            <p className="mp-sub">{version} · {date}</p>
          </div>
        </div>

        <nav className="mp-tabs" ref={tabsRef} aria-label={t('legal.toc.label')}>
          {TABS.map((x) => (
            <Link key={x.id} to={pub(x.path)} className={`mp-tab${tab === x.id ? ' is-active' : ''}`}
                  aria-current={tab === x.id ? 'page' : undefined}>
              {t(x.label)}
            </Link>
          ))}
        </nav>

        <div className="mp-doc-grid">{children}</div>
      </main>

      <PublicFooterBar />
    </div>
  )
}
